import { getActivationTrigger } from './activation';
import { EVENT_SITES } from './analytics';
import { copilotItly, webItly as iteratively } from './iteratively-slim';

// Keyed by event name and module scoped: each offending event warns and reports
// once per page load, not once per call site. Prod has no sampling in front of
// this, so per-call reporting would be unbounded.
const reportedMissingEventSites = new Set();

export default function useIteratively({
  captureException,
  capturePostHogEvent,
  environment,
  useCopilotStore = null,
  uuid,
}) {
  function reportMissingEventSite(eventName) {
    const name = eventName || 'unknown';

    if (!reportedMissingEventSites.has(name)) {
      reportedMissingEventSites.add(name);
      const message = `Missing event_site for tracking event "${name}"`;
      console.warn(message);
      if (captureException) captureException(new Error(message));
    }

    if (capturePostHogEvent)
      capturePostHogEvent('missing_event_site', { tracked_event_name: name });
  }

  const appTrackingObject = (trackingObject, currentUser, currentOrg) => {
    const userEmail = currentUser?.email || '';
    const userId = currentUser?.id?.toString() || '';
    const organizationId = currentOrg?.id?.toString() || '';

    if (!trackingObject.properties) trackingObject.properties = {};
    trackingObject.properties.email = userEmail;
    trackingObject.properties.user_id = userId;
    trackingObject.properties.organization_id = organizationId;
    trackingObject.properties.device_id = uuid;

    if (!trackingObject.properties.event_site) {
      reportMissingEventSite(trackingObject.name);
      trackingObject.properties.event_site = EVENT_SITES.MISSING_EVENT_SITE;
    }
    if (useCopilotStore) {
      const copilotStore = useCopilotStore();
      copilotStore?.source
        ? (trackingObject.properties.source = copilotStore.source)
        : (trackingObject.properties.source = '');
    }
    return trackingObject;
  };

  const isInitialized = iteratively.itly.itly.options;

  const loadIteratively = () => {
    if (!isInitialized) {
      useCopilotStore
        ? copilotItly.load({ environment })
        : iteratively.load({ environment });
    }
  };
  const setTrackingOptions = (currentUser, currentOrg) => {
    const instance = useCopilotStore ? copilotItly : iteratively;
    const originalTrack = instance.itly.track.bind(instance.itly);

    instance.itly.track = (trackingObject = {}) => {
      try {
        const enriched = appTrackingObject(
          trackingObject,
          currentUser,
          currentOrg,
        );
        originalTrack(enriched);
        if (capturePostHogEvent)
          capturePostHogEvent(enriched.name, enriched.properties);

        const trigger = getActivationTrigger(enriched);
        if (trigger) {
          const activation = appTrackingObject(
            {
              name: 'organization_activated',
              properties: {
                triggering_event: trigger,
                event_site: enriched.properties.event_site,
              },
            },
            currentUser,
            currentOrg,
          );
          originalTrack(activation);
          if (capturePostHogEvent)
            capturePostHogEvent(activation.name, activation.properties);
        }
      } catch (err) {
        if (import.meta.env.DEV) console.log(err);
        else if (captureException) captureException(err);
      }
    };
  };
  const captureLoggedInUser = (currentUser, currentOrg) => {
    const itlyInstance = useCopilotStore ? copilotItly : iteratively;

    if (currentUser?.id) {
      itlyInstance.identify(currentUser.id.toString(), {
        email: currentUser.email,
      });
    }

    if (currentOrg?.id) {
      itlyInstance.group(currentOrg.id.toString(), {
        name: currentOrg.name,
      });
    }
  };

  return {
    iteratively,
    copilotItly,
    loadIteratively,
    setTrackingOptions,
    captureLoggedInUser,
  };
}
